import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { AlertCircle, RefreshCw, Upload } from 'lucide-react';
import api from '@/lib/api';
import TelaProcessando from '@/components/TelaProcessando';
import AlternadorDeVisao from './AlternadorDeVisao';
import { retirarArquivo } from './arquivoPendente';
import { COR_FUNDO, NOME_COR, coresPresentes, tintaSobre, visaoSalva, type Visao } from './cores';
import { CABECALHO, CELULA, CELULA_COR, COLUNA_PRODUTO, LINHA, TITULO_COLUNA } from './tabela';

type LinhaPrevia = {
  ordem: number;
  produto: string;
  embalagem_erp: string | null;
  embalagem: string;
  quantidade_m: number;
  cores: Record<string, number>;
  codigos_erp: string[];
};

type Previa = {
  arquivo: string;
  itens_lidos: number;
  linhas: LinhaPrevia[];
  quantidade_total_m: number;
};

const inteiro = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });
const hoje = new Date().toISOString().slice(0, 10);

/**
 * Passo 1 do fluxo: a planilha que o comprador exporta do Compumate.
 *
 * O sistema mostra primeiro o que entendeu — quantos itens do ERP viraram
 * quantas linhas de cotação — e só grava depois de conferido. Enquanto não há
 * o que conferir, a tela é a área de escolher o arquivo; assim que há, ela sai
 * da frente e a troca de arquivo vira um botão no topo.
 */
export default function ImportarCotacaoPage() {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [previa, setPrevia] = useState<Previa | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  // O overlay tem estado próprio em vez de seguir o `isPending` do react-query:
  // a leitura começa dentro de um efeito, e no duplo-mount do StrictMode o
  // observer da mutação se desliga antes da resposta chegar — a requisição
  // termina, os dados aparecem e a flag fica presa em "pendente".
  const [processando, setProcessando] = useState<'lendo' | 'criando' | null>(null);
  const [visao, setVisao] = useState<Visao>(visaoSalva);
  const [form, setForm] = useState({
    descricao: '',
    data_cotacao: hoje,
    frete: 'CIF',
    pagamento: 'À vista',
    prazo_entrega_dias: 20,
  });

  const analisar = useMutation({
    mutationFn: async (file: File) => {
      const dados = new FormData();
      dados.append('arquivo', file);

      return (await api.post<{ data: Previa }>('/importacoes/previa', dados)).data.data;
    },
    onSuccess: (dados) => {
      setProcessando(null);
      setPrevia(dados);
      setErro(null);
      setForm((atual) => ({
        ...atual,
        descricao: atual.descricao || `Cotação de ${new Date().toLocaleDateString('pt-BR')}`,
      }));
    },
    onError: (falha: { response?: { data?: { errors?: Record<string, string[]>; message?: string } } }) => {
      setProcessando(null);
      setPrevia(null);
      setErro(
        falha.response?.data?.errors?.arquivo?.[0] ??
          falha.response?.data?.message ??
          'Não foi possível ler a planilha.',
      );
    },
  });

  const criar = useMutation({
    mutationFn: async () => {
      if (!arquivo) throw new Error('sem arquivo');

      const dados = new FormData();
      dados.append('arquivo', arquivo);
      Object.entries(form).forEach(([campo, valor]) => dados.append(campo, String(valor)));

      return (await api.post<{ data: { id: number } }>('/importacoes', dados)).data.data;
    },
    onSuccess: (cotacao) => navigate(`/cotacoes/${cotacao.id}`),
    onError: (falha: { response?: { data?: { errors?: Record<string, string[]>; message?: string } } }) => {
      setProcessando(null);
      const erros = falha.response?.data?.errors;
      setErro(erros ? Object.values(erros)[0][0] : (falha.response?.data?.message ?? 'Não foi possível criar a cotação.'));
    },
  });

  const escolher = (file: File | null) => {
    setArquivo(file);
    setPrevia(null);
    setErro(null);

    if (file) {
      setProcessando('lendo');
      analisar.mutate(file);
    }
  };

  // Quando o comprador já escolheu a planilha na lista de cotações, ela chega
  // aqui pronta e a leitura começa sozinha.
  useEffect(() => {
    const vindoDaLista = retirarArquivo();
    if (vindoDaLista) escolher(vindoDaLista);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cores = coresPresentes(previa?.linhas ?? []);

  return (
    <div className="px-lg py-lg">
      {processando === 'lendo' && (
        <TelaProcessando
          titulo="Processando planilha…"
          detalhe="Agrupando os itens do ERP por produto e somando as cores. Não feche esta janela."
        />
      )}

      {processando === 'criando' && (
        <TelaProcessando
          titulo="Criando a cotação…"
          detalhe="Gravando as linhas, as cores e a primeira rodada."
        />
      )}

      <div className="mb-lg flex flex-wrap items-start justify-between gap-md">
        <div className="min-w-0">
          <h1 className="text-[18px] font-bold text-ink">Pedido de cotação</h1>
          <p className="text-[13px] text-muted">
            {previa
              ? 'Modelo gerado atualmente na exportação do sistema Compumate (semelhantes)'
              : 'O ERP exporta um item por cor e por marca. O sistema agrupa em uma linha por produto, somando as cores — que é o formato que vai para os fornecedores.'}
          </p>
        </div>

        {previa && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="inline-flex shrink-0 items-center gap-xs rounded-md border border-border bg-surface px-md py-sm text-[13px] font-bold text-ink hover:border-accent"
          >
            <RefreshCw size={15} />
            Trocar arquivo
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept=".xls,.xlsx"
        className="sr-only"
        onChange={(evento) => {
          escolher(evento.target.files?.[0] ?? null);
          evento.target.value = '';
        }}
      />

      {!previa && (
        <div className="rounded-lg border border-border bg-surface p-lg">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="flex w-full items-center gap-md rounded-lg border-2 border-dashed border-border px-lg py-lg text-left hover:border-accent"
          >
            <Upload size={20} className="shrink-0 text-muted" />
            <span>
              <span className="block text-[14px] font-bold text-ink">
                {arquivo ? arquivo.name : 'Escolher a planilha exportada do ERP'}
              </span>
              <span className="block text-[12px] text-muted">
                {arquivo ? 'Clique para trocar o arquivo' : 'Arquivo .xls ou .xlsx'}
              </span>
            </span>
          </button>

          {erro && (
            <p className="mt-md flex items-start gap-sm rounded-md border border-danger/30 bg-danger/5 px-md py-sm text-[13px] text-danger">
              <AlertCircle size={15} className="mt-[2px] shrink-0" />
              {erro}
            </p>
          )}
        </div>
      )}

      {previa && (
        <>
          <div className="mb-sm flex flex-wrap items-center justify-between gap-sm">
            <p className="text-[12px] text-muted">
              {cores.length} {cores.length === 1 ? 'cor usada' : 'cores usadas'} nesta cotação
            </p>
            <AlternadorDeVisao visao={visao} aoTrocar={setVisao} />
          </div>

          <div className="grid items-start gap-md lg:grid-cols-[1fr_282px]">
            <div className="overflow-x-auto rounded-lg border border-border bg-surface">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className={CABECALHO}>
                    <th className={`${TITULO_COLUNA} ${COLUNA_PRODUTO} text-left`}>Produto</th>
                    <th className={`${TITULO_COLUNA} whitespace-nowrap text-right`}>Quantidade</th>

                    {visao === 'colunas' ? (
                      cores.map((cor) => (
                        <th
                          key={cor}
                          title={NOME_COR[cor] ?? cor}
                          // A borda é o que faz o branco e o cristal existirem
                          // como célula: sem ela, somem no fundo da tabela.
                          className="border border-border px-xs py-sm text-right font-bold normal-case tracking-normal"
                          style={{ background: COR_FUNDO[cor] ?? '#cbd5e1', color: tintaSobre(cor) }}
                        >
                          {cor}
                        </th>
                      ))
                    ) : (
                      <th className={`${TITULO_COLUNA} text-left`}>Cores somadas</th>
                    )}

                    <th className={`${TITULO_COLUNA} text-left`}>Embalagem</th>
                  </tr>
                </thead>
                <tbody>
                  {previa.linhas.map((linha) => (
                    <tr key={linha.ordem} className={LINHA}>
                      <td className={`${CELULA} ${COLUNA_PRODUTO}`}>
                        <span className="block text-ink">{linha.produto}</span>
                        <span className="text-[11px] text-muted">
                          {linha.codigos_erp.length} {linha.codigos_erp.length === 1 ? 'item' : 'itens'} do ERP
                        </span>
                      </td>
                      <td className={`${CELULA} whitespace-nowrap text-right font-bold tabular-nums text-ink`}>
                        {inteiro.format(linha.quantidade_m)} m
                      </td>

                      {visao === 'colunas' ? (
                        cores.map((cor) => (
                          <td key={cor} className={`${CELULA_COR} text-ink`}>
                            {linha.cores[cor] ? inteiro.format(linha.cores[cor]) : ''}
                          </td>
                        ))
                      ) : (
                        <td className={CELULA}>
                          <span className="flex flex-wrap gap-xs">
                            {Object.entries(linha.cores).map(([cor, quantidade]) => (
                              <span
                                key={cor}
                                title={NOME_COR[cor] ?? cor}
                                className="inline-flex items-center gap-xs rounded border border-border bg-surface px-xs text-[11px] tabular-nums"
                              >
                                <i
                                  className="block size-2 rounded-[2px]"
                                  style={{ background: COR_FUNDO[cor] ?? '#cbd5e1' }}
                                />
                                {inteiro.format(quantidade)}
                              </span>
                            ))}
                          </span>
                        </td>
                      )}

                      <td className={CELULA}>
                        {linha.embalagem ? (
                          <span className="text-[12px] leading-tight text-muted">{linha.embalagem}</span>
                        ) : (
                          <span className="text-[11px] text-danger">a definir</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <form
              className="overflow-hidden rounded-lg border border-border bg-surface"
              onSubmit={(evento) => {
                evento.preventDefault();
                setProcessando('criando');
                criar.mutate();
              }}
            >
              <h2 className="bg-faixa px-md py-sm text-[13px] font-bold text-white">Dados da cotação</h2>

              <div className="p-md">
                <dl className="mb-md grid grid-cols-3 gap-xs rounded-md border border-border bg-dominant px-sm py-sm text-center">
                  <div>
                    <dt className="text-[10px] uppercase tracking-wide text-muted">Itens do ERP</dt>
                    <dd className="text-[15px] font-bold tabular-nums text-ink">{previa.itens_lidos}</dd>
                  </div>
                  <div>
                    <dt className="text-[10px] uppercase tracking-wide text-muted">Linhas</dt>
                    <dd className="text-[15px] font-bold tabular-nums text-ink">{previa.linhas.length}</dd>
                  </div>
                  <div>
                    <dt className="text-[10px] uppercase tracking-wide text-muted">Metros</dt>
                    <dd className="text-[15px] font-bold tabular-nums text-ink">
                      {inteiro.format(previa.quantidade_total_m)}
                    </dd>
                  </div>
                </dl>

                {erro && (
                  <p className="mb-md flex items-start gap-sm rounded-md border border-danger/30 bg-danger/5 px-sm py-sm text-[12px] text-danger">
                    <AlertCircle size={14} className="mt-[2px] shrink-0" />
                    {erro}
                  </p>
                )}

                <label className="block text-[12px] text-muted">
                  Descrição
                  <input
                    required
                    value={form.descricao}
                    onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                    className="mt-xs w-full rounded-md border border-border px-sm py-xs text-[13px] text-ink"
                  />
                </label>

                <label className="mt-sm block text-[12px] text-muted">
                  Data
                  <input
                    required
                    type="date"
                    value={form.data_cotacao}
                    onChange={(e) => setForm({ ...form, data_cotacao: e.target.value })}
                    className="mt-xs w-full rounded-md border border-border px-sm py-xs text-[13px] text-ink"
                  />
                </label>

                <div className="grid grid-cols-2 gap-sm">
                  <label className="mt-sm block text-[12px] text-muted">
                    Frete
                    <input
                      value={form.frete}
                      onChange={(e) => setForm({ ...form, frete: e.target.value })}
                      className="mt-xs w-full rounded-md border border-border px-sm py-xs text-[13px] text-ink"
                    />
                  </label>
                  <label className="mt-sm block text-[12px] text-muted">
                    Entrega (dias)
                    <input
                      type="number"
                      min={1}
                      value={form.prazo_entrega_dias}
                      onChange={(e) => setForm({ ...form, prazo_entrega_dias: Number(e.target.value) })}
                      className="mt-xs w-full rounded-md border border-border px-sm py-xs text-[13px] tabular-nums text-ink"
                    />
                  </label>
                </div>

                <label className="mt-sm block text-[12px] text-muted">
                  Pagamento
                  <input
                    value={form.pagamento}
                    onChange={(e) => setForm({ ...form, pagamento: e.target.value })}
                    className="mt-xs w-full rounded-md border border-border px-sm py-xs text-[13px] text-ink"
                  />
                </label>

                <button
                  type="submit"
                  disabled={criar.isPending}
                  className="mt-md w-full rounded-md bg-accent px-md py-sm text-[13px] font-bold text-white disabled:opacity-60"
                >
                  {criar.isPending ? 'Criando…' : 'Criar cotação'}
                </button>
                <p className="mt-sm text-[11px] text-muted">
                  O número da cotação é gerado ao criar. A planilha original fica guardada junto dela, como registro da
                  origem.
                </p>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
}
